import { useState } from 'react';
import React from 'react';
import {
  useGetDeviceSettingsQuery,
  useSetDeviceSettingsMutation,
  useDeleteDeviceSettingMutation,
  useUpdateDeviceSettingMutation,
  useAddWaterBubbleMutation,
  useDeleteWaterBubbleMutation,
  useUpdateWaterBubbleMutation,
  useUpdateDeviceDefaultValuesMutation,
} from '../state/api';
import { FaPencilAlt, FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import { Alert } from '@mui/material';
import styles from './DeviceSettings.module.css';

type ViewMode = 'humidity' | 'waterbubble';

export default function DeviceSettings({
  deviceId,
  deviceName,
  accessLevel = 'full',
}: {
  deviceId: string;
  deviceName?: string;
  accessLevel?: 'read' | 'full';
}) {
  const isReadOnly = accessLevel === 'read';
  const { data, isLoading, refetch } = useGetDeviceSettingsQuery(deviceId);
  const [setDeviceSettings] = useSetDeviceSettingsMutation();
  const [deleteDeviceSetting] = useDeleteDeviceSettingMutation();
  const [updateDeviceSetting] = useUpdateDeviceSettingMutation();
  const [addWaterBubble] = useAddWaterBubbleMutation();
  const [deleteWaterBubble] = useDeleteWaterBubbleMutation();
  const [updateWaterBubble] = useUpdateWaterBubbleMutation();
  const [updateDeviceDefaultValues] = useUpdateDeviceDefaultValuesMutation();

  const [viewMode, setViewMode] = useState<ViewMode>('humidity');

  const schedules: any[] = data?.schedules ?? [];
  const waterBubbles: any[] = data?.waterBubble ?? [];

  // Get device name from prop or from settings data
  const displayName = deviceName || data?.name || deviceId;

  const nowPlus5 = new Date(Date.now() + 5 * 60 * 1000);
  const formatLocal = (date: Date) => {
    const tzOffset = date.getTimezoneOffset() * 60000; // offset v ms
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  // Convert UTC date string from database to local datetime-local format
  const formatUTCToLocal = (utcDateString: string) => {
    // Create date from UTC string - JavaScript Date automatically handles this
    const date = new Date(utcDateString);

    // getTimezoneOffset() returns offset in minutes (negative for timezones ahead of UTC)
    // For Czech (UTC+1 or UTC+2), it returns -60 or -120
    // To convert UTC to local: we need to ADD hours, so we SUBTRACT the negative offset
    const tzOffsetMinutes = date.getTimezoneOffset(); // e.g., -60 for UTC+1
    const localTime = date.getTime() - tzOffsetMinutes * 60000;
    const localDate = new Date(localTime);

    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Humidity state
  const [hum, setHum] = useState<number>(90);
  const [from, setFrom] = useState<string>(formatLocal(nowPlus5));
  const [to, setTo] = useState<string>('');

  const [editId, setEditId] = useState<string | null>(null);
  const [editHum, setEditHum] = useState<number>(0);
  const [editFrom, setEditFrom] = useState<string>('');
  const [editTo, setEditTo] = useState<string>('');

  const [errors, setErrors] = useState<{ from?: string; to?: string }>({});

  // WaterBubble state
  const [bubbleTime, setBubbleTime] = useState<string>('08:00');
  const [bubbleDuration, setBubbleDuration] = useState<number>(22000);
  const [editBubbleIndex, setEditBubbleIndex] = useState<number | null>(null);
  const [editBubbleTime, setEditBubbleTime] = useState<string>('');
  const [editBubbleDuration, setEditBubbleDuration] = useState<number>(0);

  // Default values state
  const [defaultHumidity, setDefaultHumidity] = useState<number>(
    data?.defaultValues?.humidity ?? 95,
  );
  const [defaultDivergence, setDefaultDivergence] = useState<number>(
    data?.defaultValues?.divergence ?? 1,
  );
  const [isEditingDefaults, setIsEditingDefaults] = useState<boolean>(false);

  // Update default values when data changes
  React.useEffect(() => {
    if (data?.defaultValues) {
      setDefaultHumidity(data.defaultValues.humidity ?? 95);
      setDefaultDivergence(data.defaultValues.divergence ?? 1);
    }
  }, [data]);

  const handleUpdateDefaults = async () => {
    try {
      await updateDeviceDefaultValues({
        deviceId,
        humidity: defaultHumidity,
        divergence: defaultDivergence,
      });
      setIsEditingDefaults(false);
      refetch();
    } catch (error) {
      console.error('Failed to update default values:', error);
    }
  };

  const now = formatLocal(new Date());

  const validateDates = (start: string, end: string, excludeId?: string) => {
    const newErrors: { from?: string; to?: string } = {};
    const startTs = new Date(start).getTime();
    const endTs = new Date(end).getTime();
    const nowTs = Date.now();

    if (startTs < nowTs) newErrors.from = 'Nelze zadat minulý čas';
    if (endTs <= startTs) newErrors.to = "'Do' musí být po 'Od'";

    // kontrola překrytí
    const overlap = schedules.some((s) => {
      if (s._id === excludeId) return false;
      const sFrom = new Date(s.from).getTime();
      const sTo = new Date(s.to).getTime();
      return startTs < sTo && endTs > sFrom;
    });
    if (overlap) newErrors.to = 'Interval se překrývá s existujícím záznamem';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateDates(from, to)) return;

    // Convert datetime-local string to ISO string
    // datetime-local format: "YYYY-MM-DDTHH:mm" (no timezone, treated as local)
    // We need to create a date that represents the local time entered by the user
    // and store it in UTC. The key is: if user enters 15:29 local, we want to store
    // it so it displays as 15:29 local when read back.

    // Parse the datetime-local string as local time
    const fromDate = new Date(from);
    const toDate = new Date(to);

    // Convert to ISO string (this will convert local to UTC)
    // This is correct: 15:29 local (UTC+1) becomes 14:29 UTC
    // When we read it back and convert to local, it becomes 15:29 again
    const fromISO = fromDate.toISOString();
    const toISO = toDate.toISOString();

    await setDeviceSettings({
      deviceId,
      hum,
      from: fromISO,
      to: toISO,
    });

    setHum(50);
    setFrom(formatLocal(nowPlus5));
    setTo('');
    setErrors({});
    refetch();
  };

  const handleUpdate = async (scheduleId: string) => {
    if (!validateDates(editFrom, editTo, editId || undefined)) return;

    // Convert datetime-local string to ISO string
    const fromDate = new Date(editFrom);
    const toDate = new Date(editTo);

    const fromISO = fromDate.toISOString();
    const toISO = toDate.toISOString();

    await updateDeviceSetting({
      deviceId,
      scheduleId,
      hum: editHum,
      from: fromISO,
      to: toISO,
    });
    setEditId(null);
    setErrors({});
    refetch();
  };

  const handleDelete = async (scheduleId: string) => {
    await deleteDeviceSetting({ deviceId, scheduleId });
    refetch();
  };

  const startEdit = (s: any) => {
    setEditId(s._id);
    setEditHum(s.hum);
    // Convert UTC date from database to local datetime-local format
    setEditFrom(formatUTCToLocal(s.from));
    setEditTo(formatUTCToLocal(s.to));
    setErrors({});
  };

  // WaterBubble handlers
  const handleBubbleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bubbleTime || bubbleDuration <= 0) return;

    await addWaterBubble({
      deviceId,
      time: bubbleTime,
      duration: bubbleDuration,
    });

    setBubbleTime('08:00');
    setBubbleDuration(22000);
    refetch();
  };

  const handleBubbleDelete = async (index: number) => {
    await deleteWaterBubble({ deviceId, bubbleIndex: index });
    refetch();
  };

  const handleBubbleUpdate = async (index: number) => {
    await updateWaterBubble({
      deviceId,
      bubbleIndex: index,
      time: editBubbleTime,
      duration: editBubbleDuration,
    });
    setEditBubbleIndex(null);
    refetch();
  };

  const startEditBubble = (bubble: any, index: number) => {
    setEditBubbleIndex(index);
    setEditBubbleTime(bubble.time);
    setEditBubbleDuration(bubble.duration);
  };

  if (isLoading) return <p>Načítám nastavení...</p>;

  // Sort schedules: active first, then newest first
  const sortedSchedules = [...schedules].sort((a: any, b: any) => {
    const nowTs = Date.now();
    const aFrom = new Date(a.from).getTime();
    const aTo = new Date(a.to).getTime();
    const bFrom = new Date(b.from).getTime();
    const bTo = new Date(b.to).getTime();

    const aIsActive = aFrom <= nowTs && nowTs <= aTo;
    const bIsActive = bFrom <= nowTs && nowTs <= bTo;

    // Active schedules first
    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // If both active or both not active, sort by newest first (using _id timestamp or from date)
    const getTimestamp = (schedule: any) => {
      if (schedule._id && schedule._id.length >= 8) {
        try {
          const timestamp = parseInt(schedule._id.substring(0, 8), 16) * 1000;
          if (!isNaN(timestamp)) return timestamp;
        } catch (e) {
          // Fallback to from date
        }
      }
      return new Date(schedule.from).getTime();
    };

    const aTimestamp = getTimestamp(a);
    const bTimestamp = getTimestamp(b);

    return bTimestamp - aTimestamp;
  });

  return (
    <div className={styles.deviceSettings}>
      {isReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Toto zařízení je sdílené s oprávněním pouze ke čtení. Nemůžete měnit nastavení.
        </Alert>
      )}
      <div className={styles.headerRow}>
        <h3>
          {viewMode === 'humidity' ? 'Nastavení vlhkosti' : 'Záplava'} – {displayName}
        </h3>
        <div className={styles.toggleButtons}>
          <button
            className={`${styles.toggleButton} ${viewMode === 'humidity' ? styles.active : ''}`}
            onClick={() => setViewMode('humidity')}
          >
            Vlhkost
          </button>
          <button
            className={`${styles.toggleButton} ${viewMode === 'waterbubble' ? styles.active : ''}`}
            onClick={() => setViewMode('waterbubble')}
          >
            Záplava
          </button>
        </div>
      </div>
      <div className={styles.deviceId}>ID: {deviceId}</div>

      {/* Default Values Section */}
      <div className={styles.defaultValuesSection}>
        <h4 className={styles.sectionTitle}>Výchozí hodnoty</h4>
        {isEditingDefaults ? (
          <div className={styles.form}>
            <div className={styles.inputWrapper}>
              <label>Výchozí vlhkost (%):</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={defaultHumidity}
                onChange={(e) => setDefaultHumidity(parseFloat(e.target.value) || 95)}
                disabled={isReadOnly}
              />
            </div>
            <div className={styles.inputWrapper}>
              <label>Divergence (%):</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={defaultDivergence}
                onChange={(e) => setDefaultDivergence(parseFloat(e.target.value) || 1)}
                disabled={isReadOnly}
              />
            </div>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleUpdateDefaults}
                className={styles.saveButton}
                disabled={isReadOnly}
              >
                <FaCheck /> Uložit
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditingDefaults(false);
                  setDefaultHumidity(data?.defaultValues?.humidity ?? 95);
                  setDefaultDivergence(data?.defaultValues?.divergence ?? 1);
                }}
                className={styles.cancelButton}
              >
                <FaTimes /> Zrušit
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.defaultValuesDisplay}>
            <div className={styles.defaultValueItem}>
              <span className={styles.label}>Výchozí vlhkost:</span>
              <span className={styles.value}>{data?.defaultValues?.humidity ?? 95}%</span>
            </div>
            <div className={styles.defaultValueItem}>
              <span className={styles.label}>Divergence:</span>
              <span className={styles.value}>{data?.defaultValues?.divergence ?? 1}%</span>
            </div>
            <button
              type="button"
              onClick={() => setIsEditingDefaults(true)}
              className={styles.actionButton}
              disabled={isReadOnly}
            >
              <FaPencilAlt /> Upravit
            </button>
          </div>
        )}
      </div>

      {viewMode === 'humidity' ? (
        <>
          <form onSubmit={handleSubmit} className={styles.form}>
            <input
              type="number"
              value={hum}
              onChange={(e) => setHum(Number(e.target.value))}
              placeholder="Vlhkost %"
              min={0}
              max={100}
              required
              disabled={isReadOnly}
            />
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              min={now}
              required
              disabled={isReadOnly}
            />
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from || now}
              required
              disabled={isReadOnly}
            />
            <button type="submit" disabled={isReadOnly}>
              Přidat
            </button>
          </form>

          {errors.from && <div className={styles.error}>{errors.from}</div>}
          {errors.to && <div className={styles.error}>{errors.to}</div>}

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Vlhk.</th>
                  <th>Od</th>
                  <th>Do</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {sortedSchedules.map((s: any) => {
                  const nowTs = Date.now();
                  const fromDate = new Date(s.from).getTime();
                  const toDate = new Date(s.to).getTime();
                  const isPast = toDate < nowTs;
                  const isActive = fromDate <= nowTs && nowTs <= toDate;

                  return (
                    <tr
                      key={s._id}
                      className={
                        isPast ? styles.pastSchedule : isActive ? styles.activeSchedule : ''
                      }
                    >
                      {editId === s._id ? (
                        <>
                          <td>
                            <input
                              type="number"
                              value={editHum}
                              onChange={(e) => setEditHum(Number(e.target.value))}
                              max={100}
                            />
                          </td>
                          <td>
                            <input
                              type="datetime-local"
                              value={editFrom}
                              min={now}
                              onChange={(e) => setEditFrom(e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="datetime-local"
                              value={editTo}
                              min={editFrom || now}
                              onChange={(e) => setEditTo(e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              onClick={() => handleUpdate(s._id)}
                              className={styles.saveButton}
                            >
                              <FaCheck />
                            </button>
                            <button onClick={() => setEditId(null)} className={styles.cancelButton}>
                              <FaTimes />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{s.hum}</td>
                          <td>
                            {new Date(s.from).toLocaleString()}
                            {isPast && <span className={styles.pastLabel}> (proběhlo)</span>}
                            {isActive && <span className={styles.activeLabel}> (probíhá)</span>}
                          </td>
                          <td>{new Date(s.to).toLocaleString()}</td>
                          <td>
                            <button
                              onClick={() => startEdit(s)}
                              className={styles.actionButton}
                              disabled={isReadOnly}
                            >
                              <FaPencilAlt />
                            </button>
                            <button
                              onClick={() => handleDelete(s._id)}
                              className={styles.deleteButton}
                              disabled={isReadOnly}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <form onSubmit={handleBubbleSubmit} className={styles.form}>
            <input
              type="time"
              value={bubbleTime}
              onChange={(e) => setBubbleTime(e.target.value)}
              required
              disabled={isReadOnly}
            />
            <input
              type="number"
              value={bubbleDuration}
              onChange={(e) => setBubbleDuration(Number(e.target.value))}
              placeholder="Délka (ms)"
              min={0}
              required
              disabled={isReadOnly}
            />
            <button type="submit" disabled={isReadOnly}>
              Přidat
            </button>
          </form>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Čas</th>
                  <th>Délka (ms)</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {waterBubbles.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>
                      Žádné záplavy
                    </td>
                  </tr>
                ) : (
                  waterBubbles.map((bubble: any, index: number) => (
                    <tr key={index}>
                      {editBubbleIndex === index ? (
                        <>
                          <td>
                            <input
                              type="time"
                              value={editBubbleTime}
                              onChange={(e) => setEditBubbleTime(e.target.value)}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editBubbleDuration}
                              onChange={(e) => setEditBubbleDuration(Number(e.target.value))}
                              min={0}
                              required
                            />
                          </td>
                          <td>
                            <button onClick={() => handleBubbleUpdate(index)}>
                              <FaCheck />
                            </button>
                            <button onClick={() => setEditBubbleIndex(null)}>
                              <FaTimes />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{bubble.time}</td>
                          <td>{bubble.duration}</td>
                          <td>
                            <button
                              onClick={() => startEditBubble(bubble, index)}
                              disabled={isReadOnly}
                            >
                              <FaPencilAlt />
                            </button>
                            <button onClick={() => handleBubbleDelete(index)} disabled={isReadOnly}>
                              ❌
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
